/**
 * One stage of a change page: the stage's number and name, a title, its state,
 * and the body. The four sections of the page are this one shell, so the eye
 * finds the state in the same place on every stage.
 */
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { STAGES, STAGE_LABEL, type ChangeState, type Stage } from '@/lib/changes/model';
import { StateBadge } from './changeBadges';

export function StageSection({
  stage,
  title,
  state,
  current,
  aside,
  children,
}: {
  stage: Stage;
  title: string;
  state: ChangeState;
  current: boolean;
  /** Right-aligned header slot, e.g. a size badge. */
  aside?: ReactNode;
  children: ReactNode;
}) {
  const n = STAGES.indexOf(stage) + 1;
  return (
    <section
      data-stage-section={stage}
      aria-labelledby={`stage-${stage}`}
      className={cn(
        'rounded-2xl border bg-white p-5 dark:bg-slate-900 sm:p-6',
        current ? 'border-violet-300 dark:border-violet-700' : 'border-slate-200 dark:border-slate-800',
      )}
    >
      <header className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
          Stage {n} · {STAGE_LABEL[stage]}
        </span>
        <h2 id={`stage-${stage}`} className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          {title}
        </h2>
        <StateBadge state={state} />
        {aside && <div className="ml-auto flex items-center gap-2">{aside}</div>}
      </header>
      {children}
    </section>
  );
}

/** A labelled block inside a stage. */
export function Block({ label, children, className }: { label: string; children: ReactNode; className?: string }) {
  return (
    <div className={cn('rounded-xl border border-slate-200 p-4 dark:border-slate-800', className)}>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">{label}</h3>
      {children}
    </div>
  );
}

/** The quiet sentence under a block: the rule it follows. */
export function Rule({ children }: { children: ReactNode }) {
  return <p className="mt-3 text-xs leading-snug text-slate-500 dark:text-slate-400">{children}</p>;
}

/** A table scrolls inside its card at phone width; the page never scrolls sideways. */
export function TableScroll({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('overflow-x-auto', className)}>{children}</div>;
}

export const TABLE = 'w-full text-left text-sm';
export const TH = 'pb-1.5 pr-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400';
export const TD = 'border-t border-slate-100 py-1.5 pr-3 align-top text-slate-700 dark:border-slate-800 dark:text-slate-300';
export const CODE = 'rounded bg-slate-100 px-1 py-0.5 font-mono text-[11px] text-slate-700 dark:bg-slate-800 dark:text-slate-300';
